import UtilityService from "./UtilityService";

const PlaybookInputFieldService = {
	/**
	 * The run inputs a playbook version declares, in the order the version lists them.
	 * A version with none, or no version at all, gives an empty list.
	 *
	 * @param {Object} params
	 * @param {Object} params.version the playbook version, as the API returns it (camelCase inputFields)
	 * @return {Array} [{ key, label, kind, isRequired, defaultValue, choices, requiredWith, help, fills }]
	 */
	fieldsOf({ version }) {
		if (!version) return [];
		const declaredFields = version.inputFields || [];
		return declaredFields;
	},

	/**
	 * What the run form starts with: each field's default, written the way its editor shows it.
	 * Money is shown with thousands separators and two decimals ("12,345.67"), through
	 * UtilityService.parseAmount and formatCurrency, so a default the guide stored as "12345.67"
	 * reads like an amount. Yes/no is a boolean, and the words "true" and "false" (any case, once
	 * trimmed) are read as one, because the guide's strict schema stores every default as a string.
	 * Every other kind is a string. A field with no default starts as an empty string, so an
	 * unanswered yes/no is told apart from a "No".
	 *
	 * @param {Object} params
	 * @param {Array} params.fields the version's declared fields
	 * @return {Object} { <key>: <starting value> }
	 */
	initialValues({ fields }) {
		const declaredFields = fields || [];
		const isEmptyValue = value => value === null || value === undefined || (typeof value === "string" && value.trim() === "");
		const startingValues = {};
		declaredFields.forEach(field => {
			const defaultValue = field.defaultValue;
			if (isEmptyValue(defaultValue)) {
				startingValues[field.key] = "";
				return;
			}
			if (field.kind === "money") {
				const amount = UtilityService.parseAmount(defaultValue);
				const shownAmount = UtilityService.formatCurrency(amount);
				startingValues[field.key] = shownAmount;
				return;
			}
			if (field.kind === "yesNo") {
				const isBoolean = typeof defaultValue === "boolean";
				const writtenAnswer = String(defaultValue).trim().toLowerCase();
				const isWrittenYesOrNo = writtenAnswer === "true" || writtenAnswer === "false";
				if (!isBoolean && !isWrittenYesOrNo) {
					startingValues[field.key] = "";
					return;
				}
				const answer = isBoolean ? defaultValue : writtenAnswer === "true";
				startingValues[field.key] = answer;
				return;
			}
			const shownDefault = String(defaultValue);
			startingValues[field.key] = shownDefault;
		});
		return startingValues;
	},

	/**
	 * The values a run is started with: each declared field's value as the person typed it (strings
	 * and booleans untouched, a "No" kept as false), with empty values left out so the API can apply
	 * the field's default. Keys that are not declared fields are not sent. The API checks and types
	 * every value (playbookInputFieldRules.readFieldValues); this only gathers them.
	 *
	 * @param {Object} params
	 * @param {Array} params.fields the version's declared fields
	 * @param {Object} params.values { <key>: <what the form holds> }
	 * @return {Object} { <key>: <value> }
	 */
	valuesForRun({ fields, values }) {
		const declaredFields = fields || [];
		const formValues = values || {};
		const isEmptyValue = value => value === null || value === undefined || (typeof value === "string" && value.trim() === "");
		const runValues = {};
		declaredFields.forEach(field => {
			const value = formValues[field.key];
			if (isEmptyValue(value)) return;
			runValues[field.key] = value;
		});
		return runValues;
	},

	/**
	 * The fields that keep the Start button disabled: a required field that is empty and has no
	 * default, and a field that is empty with no default while its requiredWith partner is filled
	 * (by the person or by the partner's default). A "No" (false) counts as filled. The API repeats
	 * every check and is the authority; this only spares a person a refused start.
	 *
	 * @param {Object} params
	 * @param {Array} params.fields the version's declared fields
	 * @param {Object} params.values { <key>: <what the form holds> }
	 * @return {Array} the fields still missing, in the version's order
	 */
	missingRequired({ fields, values }) {
		const declaredFields = fields || [];
		const formValues = values || {};
		const isEmptyValue = value => value === null || value === undefined || (typeof value === "string" && value.trim() === "");
		const isFilled = field => {
			const hasValue = !isEmptyValue(formValues[field.key]);
			const hasDefault = !isEmptyValue(field.defaultValue);
			const filled = hasValue || hasDefault;
			return filled;
		};
		const missingFields = declaredFields.filter(field => {
			if (isFilled(field)) return false;
			const partner = declaredFields.find(candidate => candidate.key === field.requiredWith);
			const isPartnerFilled = !!partner && isFilled(partner);
			const isMissing = !!field.isRequired || isPartnerFilled;
			return isMissing;
		});
		return missingFields;
	},

	/**
	 * The words the run report shows for one value used. The API stores typed values on the run
	 * (inputs.fieldValues): money as whole cents, shown as dollars through
	 * UtilityService.formatSignedCurrency (formatCurrency with a "$"), a month 1 to 12 by its English
	 * name, yes/no as "Yes" or "No", a choice by its plain words from field.choiceLabels, and a date
	 * or text exactly as stored. A choice the field has no label for (an older draft, a guide
	 * suggestion before a draft, a label map missing that choice) is shown as its stored code, so it
	 * stays visible. The form's options and the Run inputs card read choices through this too. A value
	 * the run does not hold (an optional field left empty) reads "Not given"; when the field says what
	 * an empty value means (field.emptyMeans, e.g. the claim fields' CRA claim code 1), that meaning
	 * follows in brackets, word for word, since it starts with a proper name ("CRA") that must keep
	 * its capitals. A month outside 1 to 12 is shown as stored, so a wrong value stays visible.
	 *
	 * @param {Object} params
	 * @param {Object} params.field the declared field
	 * @param {*} params.value the typed value from inputs.fieldValues
	 * @return {String}
	 */
	describeFieldValue({ field, value }) {
		const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
		const isEmptyValue = value === null || value === undefined || (typeof value === "string" && value.trim() === "");
		if (isEmptyValue) {
			const emptyMeaning = typeof field.emptyMeans === "string" ? field.emptyMeans.trim() : "";
			const notGivenWords = emptyMeaning ? `Not given (${emptyMeaning})` : "Not given";
			return notGivenWords;
		}
		if (field.kind === "money") {
			const dollars = Number(value) / 100;
			const shownAmount = UtilityService.formatSignedCurrency({ value: dollars });
			return shownAmount;
		}
		if (field.kind === "month") {
			const month = Number(value);
			const isMonth = Number.isInteger(month) && month >= 1 && month <= 12;
			const shownMonth = isMonth ? monthNames[month - 1] : String(value);
			return shownMonth;
		}
		if (field.kind === "yesNo") {
			const writtenAnswer = String(value).trim().toLowerCase();
			const isYes = value === true || writtenAnswer === "true";
			const shownAnswer = isYes ? "Yes" : "No";
			return shownAnswer;
		}
		if (field.kind === "choice") {
			const storedChoice = String(value);
			const choiceLabels = field.choiceLabels || {};
			const hasLabel = Object.prototype.hasOwnProperty.call(choiceLabels, storedChoice) && !!choiceLabels[storedChoice];
			const shownChoice = hasLabel ? String(choiceLabels[storedChoice]) : storedChoice;
			return shownChoice;
		}
		const shownValue = String(value);
		return shownValue;
	},

	/**
	 * Which editor the run form uses for a kind: "amount" for money and number (the tk-input with a
	 * decimal keypad), "select" for a choice and a month, "checkbox" for yes/no, "date" for a date and
	 * "text" for text. A kind outside the seven the API allows gives null, so a new kind shows up as
	 * missing rather than as a text box.
	 *
	 * @param {Object} params
	 * @param {String} params.kind one of inputFieldKinds.json
	 * @return {String|null}
	 */
	editorFor({ kind }) {
		const editorsByKind = { money: "amount", number: "amount", choice: "select", month: "select", yesNo: "checkbox", date: "date", text: "text" };
		const hasEditor = Object.prototype.hasOwnProperty.call(editorsByKind, kind);
		if (!hasEditor) return null;
		const editor = editorsByKind[kind];
		return editor;
	},
};

export default PlaybookInputFieldService;
